import type { ReactNode } from 'react';
import type { AhPayload, AhVenue, AhTier } from '../ahReport';
import { Donut } from '../charts';
import { fmtNum, gameLabel, visibleDetails, grandTotal, nc, pctCell, gamesCompleted } from '../utils';

type Period = 'all' | 'p1' | 'p2' | '7d';

function PeriodTag({ period }: { period: AhVenue['period'] }) {
  if (period === 'both') return <span className="period-tag-inline">P1+P2</span>;
  if (period === 'w25') return <span className="period-tag-inline w25">Wk 25</span>;
  return <span className="period-tag-inline">P{period}</span>;
}

function BrandCell({ venue }: { venue: AhVenue }) {
  const game = gameLabel(venue);
  return (
    <div className="brand-cell">
      <div className="brand-swatch" style={{ background: venue.color }} />
      <div>
        <div className="brand-name">{venue.name}</div>
        {game && <div className="brand-game">{game}</div>}
      </div>
    </div>
  );
}

function VisitsCell({ venue, maxVisits }: { venue: AhVenue; maxVisits: number }) {
  return (
    <div className="visits-bar">
      <div className="bar">
        <div className="bar-fill" style={{ width: `${(venue.visits / maxVisits) * 100}%`, background: venue.color }} />
      </div>
      {fmtNum(venue.visits)}
    </div>
  );
}

function TierTableShell({
  title,
  descriptor,
  count,
  total,
  head,
  children,
}: {
  title: string;
  descriptor?: string;
  count: number;
  total?: number;
  head: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="venues-table-wrap">
      <div className="venues-table-head">
        <div>
          <h3>{title}</h3>
          {descriptor && <div className="tier-descriptor">{descriptor}</div>}
        </div>
        <span className="meta">
          {count} venues{total != null ? ` · ${fmtNum(total)} bezoeken` : ''}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="venues-table">
          <thead>
            <tr>{head}</tr>
          </thead>
          {children}
        </table>
      </div>
    </div>
  );
}

function PremiumTable({ venues, maxVisits, onOpen }: { venues: AhVenue[]; maxVisits: number; onOpen: (v: AhVenue) => void }) {
  if (!venues.length) return null;
  const total = venues.reduce((a, v) => a + v.visits, 0);
  return (
    <TierTableShell
      title="Premium venues"
      descriptor="Eigen activatie-model — geen gameplay, wel video's, foto-acties en win-acties (zie detail)"
      count={venues.length}
      total={total}
      head={
        <>
          <th>Brand</th>
          <th>Periode</th>
          <th className="num">Total visits</th>
          <th className="num">Unique users</th>
          <th className="num">Gem. tijd</th>
          <th className="num">Tot. tijd</th>
          <th>Belangrijkste acties</th>
        </>
      }
    >
      <tbody>
        {venues.map((v) => (
          <tr key={v.id} onClick={() => onOpen(v)}>
            <td>
              <BrandCell venue={v} />
            </td>
            <td>
              <PeriodTag period={v.period} />
            </td>
            <td className="num">
              <VisitsCell venue={v} maxVisits={maxVisits} />
            </td>
            <td className="num">{nc(v.uniqueUsers)}</td>
            <td className="num">{v.avgTime}</td>
            <td className="num muted">{v.totalTime}</td>
            <td className="actions-cell">
              {visibleDetails(v)
                .map(([k]) => k)
                .join(' · ')}
            </td>
          </tr>
        ))}
      </tbody>
    </TierTableShell>
  );
}

function GameTierTable({
  title,
  descriptor,
  gtLabel,
  venues,
  maxVisits,
  onOpen,
  showPractice,
}: {
  title: string;
  descriptor: string;
  gtLabel: string;
  venues: AhVenue[];
  maxVisits: number;
  onOpen: (v: AhVenue) => void;
  showPractice: boolean;
}) {
  if (!venues.length) return null;
  const total = venues.reduce((a, v) => a + v.visits, 0);
  const gt = grandTotal(venues);
  return (
    <TierTableShell
      title={title}
      descriptor={descriptor}
      count={venues.length}
      total={total}
      head={
        <>
          <th>Brand</th>
          <th>Periode</th>
          <th className="num">Total visits</th>
          <th className="num">Unique users</th>
          <th className="num">Unique players</th>
          {showPractice && <th className="num">Practice games</th>}
          <th className="num">Total games played</th>
          <th className="num">Games completed</th>
          <th className="num">Game completion rate</th>
          <th className="num">Coupons distributed</th>
          <th className="num">Gem. tijd</th>
          <th className="num">Tot. tijd</th>
        </>
      }
    >
      <tbody>
        {venues.map((v) => (
          <tr key={v.id} onClick={() => onOpen(v)}>
            <td>
              <BrandCell venue={v} />
            </td>
            <td>
              <PeriodTag period={v.period} />
            </td>
            <td className="num">
              <VisitsCell venue={v} maxVisits={maxVisits} />
            </td>
            <td className="num">{nc(v.uniqueUsers)}</td>
            <td className="num">{nc(v.uniquePlayers)}</td>
            {showPractice && <td className="num">{nc(v.practiceStarts)}</td>}
            <td className="num">{nc(v.gamesPlayed)}</td>
            <td className="num">{nc(gamesCompleted(v))}</td>
            <td className="num">{pctCell(v.completionRate)}</td>
            <td className="num">{nc(v.vouchersGiven)}</td>
            <td className="num">{v.avgTime}</td>
            <td className="num muted">{v.totalTime}</td>
          </tr>
        ))}
        <tr className="grand-total-row">
          <td>{gtLabel}</td>
          <td></td>
          <td className="num">—</td>
          <td className="num">—</td>
          <td className="num">—</td>
          {showPractice && <td className="num">{fmtNum(gt.practice)}</td>}
          <td className="num">{fmtNum(gt.games)}</td>
          <td className="num">{fmtNum(gt.completed)}</td>
          <td className="num">—</td>
          <td className="num">{fmtNum(gt.coupons)}</td>
          <td className="num">—</td>
          <td className="num">—</td>
        </tr>
      </tbody>
    </TierTableShell>
  );
}

function BronzeTable({ venues, onOpen }: { venues: AhVenue[]; onOpen: (v: AhVenue) => void }) {
  if (!venues.length) return null;
  return (
    <TierTableShell
      title="Bronze sponsors"
      descriptor="Branding only — geen game, missions of coupons trackbaar."
      count={venues.length}
      head={
        <>
          <th>Brand</th>
          <th>Periode</th>
        </>
      }
    >
      <tbody>
        {venues.map((v) => (
          <tr key={v.id} onClick={() => onOpen(v)}>
            <td>
              <BrandCell venue={v} />
            </td>
            <td>
              <PeriodTag period={v.period} />
            </td>
          </tr>
        ))}
      </tbody>
    </TierTableShell>
  );
}

export function VenuesSection({
  venues,
  onOpen,
  overlap,
  crossVenue,
  period,
}: {
  venues: AhVenue[];
  onOpen: (v: AhVenue) => void;
  overlap: AhPayload['venueOverlap'];
  crossVenue: AhPayload['crossVenue'];
  period: Period;
}) {
  const visible = venues.filter((v) => {
    if (period === 'p1') return v.period === 1 || v.period === 'both' || v.period === 'w25';
    if (period === 'p2') return v.period === 2 || v.period === 'both';
    return true;
  });
  const noActivity = (v: AhVenue) => v.visits <= 0;
  const byTier = (t: AhTier) => visible.filter((v) => v.tier === t).sort((a, b) => Number(noActivity(a)) - Number(noActivity(b)));
  const premium = byTier('premium');
  const gold = byTier('gold');
  const silver = byTier('silver');
  const bronze = byTier('bronze');
  const maxAll = Math.max(...visible.map((v) => v.visits), 1);
  const overlapColors = ['#0E4A82', '#1B6FB8', '#3A8DDE', '#7BA9D5', '#B6CFE5'];
  const segments = overlap.map((o, i) => ({ ...o, value: o.pct, color: overlapColors[i % overlapColors.length] }));

  return (
    <section id="venues" className="section">
      <div className="section-head">
        <div>
          <div className="eyebrow">03 — Brand venues</div>
          <h2>Per tier, met grand totals</h2>
        </div>
        <div className="description">
          Elke tier heeft een eigen activatie-model en dus een eigen set metrics. Klik een rij voor de venue-details.{' '}
          {crossVenue.goldAndSilverPct.toLocaleString('nl-NL')}% van de deelnemers bezoekt zowel een gold als silver venue.
        </div>
      </div>

      <div className="tier-tables">
        <PremiumTable venues={premium} maxVisits={maxAll} onOpen={onOpen} />
        <GameTierTable
          title="Gold partners"
          descriptor="Eigen game · practice · weekly giftbox · digital missions"
          gtLabel="Grand total — Gold"
          venues={gold}
          maxVisits={maxAll}
          onOpen={onOpen}
          showPractice
        />
        <GameTierTable
          title="Silver partners"
          descriptor="Penalty Shooter · free games · weekly giftbox · digital missions"
          gtLabel="Grand total — Silver"
          venues={silver}
          maxVisits={maxAll}
          onOpen={onOpen}
          showPractice={false}
        />
        <BronzeTable venues={bronze} onOpen={onOpen} />
      </div>

      <hr className="rule-soft" style={{ marginTop: 56 }} />
      <div className="grid-2-1" style={{ marginTop: 32 }}>
        <div className="card">
          <div className="card-head">
            <h3>Hoeveel venues bezoeken gebruikers?</h3>
            <span className="meta">Multi-venue gedrag</span>
          </div>
          <div className="dist-block">
            <Donut segments={segments} size={160} thickness={22} />
            <div className="dist-list">
              {segments.map((s, i) => (
                <div key={i} className="dist-row">
                  <span className="swatch" style={{ background: s.color }} />
                  <span className="label">{s.label}</span>
                  <span className="value">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h3>Cross-venue inzicht</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 8 }}>
            <div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 44, lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                {crossVenue.goldAndSilverPct.toLocaleString('nl-NL')}%
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
                bezocht zowel een <strong>gold</strong> als <strong>silver</strong> venue
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 32, lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                {crossVenue.avgVenuesPerUser.toLocaleString('nl-NL')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>gemiddeld aantal venues per gebruiker</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
